import { useEffect, useRef, useState } from 'react';
import { PRICES, shekel } from '@/data/prices';

/**
 * 3-step process — "we know what we're doing".
 *
 * Circuit-board version (2026-10-10, owner asked for more computer-themed
 * effects): each step is a chip on a board; as the section scrolls through
 * the viewport, current runs along a PCB trace from chip to chip (SVG stroke
 * driven by scroll position) and each chip's status LED lights up when the
 * current reaches it. No pinning, no 3D — plain SVG/CSS, updated through refs
 * (no React re-render per scroll frame). Reduced motion: fully powered.
 */
const steps = [
  {
    num: '01',
    chip: 'U1',
    title: 'כתבו בוואטסאפ',
    body: 'מה קרה ואיזה מחשב זה. אם אפשר, צרפו תמונה. הייעוץ חינם: מה זה יכול להיות, והאם כדאי להביא למעבדה.',
  },
  {
    num: '02',
    chip: 'U2',
    title: 'בדיקה במעבדה, מחיר בכתב',
    body: `מביאים את המחשב למעבדה בנטור, בתיאום מראש. בדיקה במעבדה עולה ${shekel(PRICES.labCheck.min)}. אחריה מקבלים מחיר סופי בכתב, ומחליטים.`,
  },
  {
    num: '03',
    chip: 'U3',
    title: 'תיקון ו־3 חודשי אחריות',
    body: 'רוב התיקונים מסתיימים תוך 1–3 ימים. כשצריך להזמין חלק, זה יכול לקחת עד שבוע, ואתם מעודכנים לאורך הדרך.',
  },
];

// Where along the trace each chip sits (0…1) — the LED lights when current passes.
const CHIP_AT = [0.08, 0.5, 0.92];

export default function HowItWorks() {
  const sectionRef = useRef<HTMLElement>(null);
  const traceRefs = useRef<SVGPathElement[]>([]);
  const [lit, setLit] = useState(0);
  const litRef = useRef(0);

  useEffect(() => {
    const section = sectionRef.current!;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    const update = () => {
      raf = 0;
      const r = section.getBoundingClientRect();
      const vh = window.innerHeight;
      // 0 when the section's top reaches 85% of the viewport, 1 when its
      // bottom passes 55% — the current flows while you read the steps.
      const p = reduced ? 1 : Math.min(1, Math.max(0, (vh * 0.85 - r.top) / (r.height + vh * 0.3)));
      traceRefs.current.forEach((path) => {
        if (path) path.style.strokeDashoffset = String(1 - p);
      });
      const n = CHIP_AT.filter((at) => p >= at).length;
      if (n !== litRef.current) {
        litRef.current = n;
        setLit(n);
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  const trace = (el: SVGPathElement | null, i: number) => {
    if (el) traceRefs.current[i] = el;
  };

  return (
    <section ref={sectionRef} className="font-heebo relative isolate overflow-hidden bg-white py-[clamp(64px,8vw,112px)] px-[clamp(20px,5vw,80px)]">
      {/* faint board grid */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 opacity-[0.5] [background-image:linear-gradient(rgba(30,85,217,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(30,85,217,0.06)_1px,transparent_1px)] [background-size:28px_28px] [mask-image:radial-gradient(ellipse_80%_70%_at_50%_50%,black,transparent)]"
      />
      <div className="mx-auto max-w-[1280px]">
        <div className="cin-reveal mb-12 md:mb-16 max-w-2xl">
          <div className="cin-label-ed mb-4">איך זה עובד</div>
          <h2 className="cin-display-sm text-[var(--cin-ink)]">
            שלושה שלבים, <span className="cin-text-gradient-blue">בלי הפתעות בחשבון.</span>
          </h2>
        </div>

        <div className="relative">
          {/* Desktop trace: right → left through the three chips (RTL order). */}
          <svg aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-[34px] hidden h-[60px] w-full md:block" viewBox="0 0 1200 60" preserveAspectRatio="none">
            <path d="M1200 30 H960 L930 8 H640 L610 30 H560 L530 52 H260 L230 30 H0" fill="none" stroke="rgba(15,23,41,0.10)" strokeWidth="3" vectorEffect="non-scaling-stroke" />
            <path ref={(el) => trace(el, 0)} d="M1200 30 H960 L930 8 H640 L610 30 H560 L530 52 H260 L230 30 H0" pathLength={1} strokeDasharray="1" strokeDashoffset="1" fill="none" stroke="#1E55D9" strokeWidth="6" strokeLinecap="round" vectorEffect="non-scaling-stroke" className="blur-[5px] opacity-70" />
            <path ref={(el) => trace(el, 1)} d="M1200 30 H960 L930 8 H640 L610 30 H560 L530 52 H260 L230 30 H0" pathLength={1} strokeDasharray="1" strokeDashoffset="1" fill="none" stroke="#5B8CFF" strokeWidth="3" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          </svg>
          {/* Phone trace: down the right edge. */}
          <svg aria-hidden="true" className="pointer-events-none absolute right-[22px] top-0 h-full w-[20px] md:hidden" viewBox="0 0 20 1000" preserveAspectRatio="none">
            <path d="M10 0 V1000" fill="none" stroke="rgba(15,23,41,0.10)" strokeWidth="3" vectorEffect="non-scaling-stroke" />
            <path ref={(el) => trace(el, 2)} d="M10 0 V1000" pathLength={1} strokeDasharray="1" strokeDashoffset="1" fill="none" stroke="#1E55D9" strokeWidth="6" vectorEffect="non-scaling-stroke" className="blur-[4px] opacity-70" />
            <path ref={(el) => trace(el, 3)} d="M10 0 V1000" pathLength={1} strokeDasharray="1" strokeDashoffset="1" fill="none" stroke="#5B8CFF" strokeWidth="3" vectorEffect="non-scaling-stroke" />
          </svg>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {steps.map((step, idx) => {
              const on = idx < lit;
              return (
                <div key={step.num} className="relative pr-14 md:pr-0">
                  {/* the chip */}
                  <div
                    className={`absolute right-0 top-1 md:relative md:right-auto md:top-auto md:mb-6 flex h-[44px] w-[44px] md:h-[64px] md:w-[96px] items-center justify-center rounded-md border bg-[#0F1729] transition-[box-shadow,border-color] duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] ${
                      on ? 'border-[#5B8CFF] shadow-[0_0_0_4px_rgba(91,140,255,0.15),0_0_28px_rgba(30,85,217,0.45)]' : 'border-transparent shadow-none'
                    }`}
                  >
                    {/* pins */}
                    <span aria-hidden="true" className="absolute -top-[5px] inset-x-2 hidden md:flex justify-between">
                      {Array.from({ length: 7 }).map((_, i) => <span key={i} className="h-[5px] w-[4px] rounded-t-[1px] bg-[#9AA6BD]" />)}
                    </span>
                    <span aria-hidden="true" className="absolute -bottom-[5px] inset-x-2 hidden md:flex justify-between">
                      {Array.from({ length: 7 }).map((_, i) => <span key={i} className="h-[5px] w-[4px] rounded-b-[1px] bg-[#9AA6BD]" />)}
                    </span>
                    <span className="font-mono text-[13px] md:text-[15px] font-bold tracking-wider text-white/85">{step.chip}</span>
                    {/* status LED */}
                    <span
                      aria-hidden="true"
                      className={`absolute left-1.5 top-1.5 h-1.5 w-1.5 rounded-full transition-[background-color,box-shadow] duration-300 ${on ? 'bg-emerald-400 shadow-[0_0_8px_2px_rgba(52,211,153,0.8)]' : 'bg-white/20'}`}
                    />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-[#1E55D9] mb-1">שלב {step.num}</div>
                    <h3 className="text-xl font-bold text-[var(--cin-ink)] mb-3 leading-snug">{step.title}</h3>
                    <p className="text-base text-[var(--cin-muted)] leading-relaxed">{step.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
